/**
 * Utility functions for name formatting and abbreviation
 */

/**
 * Gets the abbreviation for a name to be used in avatars or chips
 * Rules:
 * - If both firstName and lastName exist: First letter of firstName + First letter of lastName
 * - If only firstName exists: First two letters of firstName
 * - If only lastName exists: First two letters of lastName
 * - If neither exists: Returns empty string
 * 
 * @param firstName - First name of the person
 * @param lastName - Last name of the person
 * @returns Abbreviated name (2 letters, uppercase)
 */
export function getNameAbbreviation(firstName?: string, lastName?: string): string {
    const fName = firstName?.trim() || '';
    const lName = lastName?.trim() || '';

    if (fName && lName) {
        // Both names exist: First letter of first name + First letter of last name
        return `${fName.charAt(0)}${lName.charAt(0)}`.toUpperCase();
    } else if (fName) {
        // Only first name exists: First two letters of first name
        return fName.substring(0, 2).toUpperCase();
    } else if (lName) {
        // Only last name exists: First two letters of last name
        return lName.substring(0, 2).toUpperCase();
    }

    // Neither exists
    return '';
}

/**
 * Gets the abbreviation from a full name string
 * Parses the full name and extracts firstName and lastName, then calls getNameAbbreviation
 * 
 * @param fullName - Full name string (e.g., "John Doe" or "John")
 * @returns Abbreviated name (2 letters, uppercase)
 */
export function getNameAbbreviationFromFullName(fullName?: string): string {
    if (!fullName || !fullName.trim()) {
        return '';
    }

    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0] || '';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

    return getNameAbbreviation(firstName, lastName);
}

/**
 * Simple hash function to convert a string to a number
 * @param str - String to hash
 * @returns Hash value as number
 */
function hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
}

/**
 * Generates a deterministic, visually distinct background color from any string.
 * The same input always produces the same color. Output colors are guaranteed
 * to have sufficient contrast for white (#fff) text (WCAG AA ≥ 4.5:1).
 *
 * Works by hashing the input into an HSL hue, then using a fixed saturation
 * and lightness range that keeps relative luminance low enough for white text.
 *
 * @param value - Any arbitrary string (name, ID, label, etc.)
 * @returns Hex color code (e.g. "#2E7D32")
 */
export function getAvatarColor(value?: string): string {
    const DEFAULT_COLOR = '#455A64';

    if (!value || value.trim() === '') {
        return DEFAULT_COLOR;
    }

    const hash = hashString(value);

    const hue = hash % 360;
    const saturation = 45 + (hash % 35);        // 45-79% — vivid but not neon
    const lightness = 50 + ((hash >>> 8) % 14); // 50-63% — dark enough for white text

    return hslToHex(hue, saturation, lightness);
}

function hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number): string => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

